import React from 'react';
import delayChangelog from './delay.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeError } from '@common/hooks/useNodeError';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

import { DelayNodeState, NodeConfig, NodeField, NodeFieldType, NodeOutput, NodeType, NodeInputType } from '../..';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Delay',
  identifier: 'Delay',
  command_id: 'utility.delay',
  icon: 'delay',
  background: '#f35c5c',
  changelog: delayChangelog,
  anchors: [
    {
      label: 'Execute',
      id: 'utility.delay.execute',
      type: 'target' as HandleType,
      variant: 'execution',
      isHandle: true,
    },
    {
      label: 'Data',
      id: 'utility.delay.input_data',
      type: 'target' as HandleType,
      variant: 'data',
      isHandle: true,
    },
    {
      label: 'Completed',
      id: 'utility.delay.completed',
      type: 'source' as HandleType,
      variant: 'execution',
    },
  ],
};

export const state: DelayNodeState = {
  delay: {
    type: NodeInputType.Number,
    value: '1',
  },
};

export const fields: NodeField[] = [
  {
    id: 'delay',
    tab: '1',
    type: NodeFieldType.NumberExpression,
    default_type: NodeInputType.Number,
    minNumber: 0,
    maxNumber: 300,
    label: 'Delay (seconds)',
    description:
      "The amount of time to delay the workflow's execution. The delay can be a fixed number of seconds or a dynamic expression.",
    placeholder: 'Time to delay in seconds.',
  },
];

export const output: NodeOutput = { default: null, dynamic: null };

export const Delay: React.FC<NodeType> = ({ id, selected, data }) => {
  const { title, background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'utility.delay') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={title} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
